import { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Card,
  Fade,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "../../components/navbar/NavBar";
import Footer from "../../components/navbar/Footer";
import { useSendContactMessage } from "../../hooks/query/useContact";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const mutation = useSendContactMessage();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Create pending toast
    const toastId = toast.loading("Sending your message...", {
      position: "top-right",
      theme: "light",
    });

    try {
      await mutation.mutateAsync(formData);

      // Update toast to success
      toast.update(toastId, {
        render: "Message sent successfully!",
        type: "success",
        isLoading: false,
        autoClose: 5000,
        closeButton: true,
        closeOnClick: true,
      });

      // Reset form
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      // Update toast to error
      toast.update(toastId, {
        render: "Failed to send message. Please try again.",
        type: "error",
        isLoading: false,
        autoClose: 5000,
        closeButton: true,
        closeOnClick: true,
      });
    }
  };

  return (
    <Box sx={{ background: "#ffffff", minHeight: "100vh", color: "#333333" }}>
      <Navbar />

      {/* ToastContainer configuration */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999 }}
      />

      <Container sx={{ pt: 15, pb: 8 }}>
        <Fade in timeout={1000}>
          <Grid container spacing={6}>
            {/* Contact Form Section */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  color: "#bb0000",
                  fontWeight: 700,
                  position: "relative",
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: -8,
                    left: 0,
                    width: "80px",
                    height: "4px",
                    background: "#bb0000",
                    borderRadius: "2px",
                  },
                }}
              >
                Contact Us
              </Typography>
              <Typography
                variant="h5"
                sx={{ mb: 4, color: "#666666", fontWeight: 300 }}
              >
                Get in Touch with Our Vastu Experts
              </Typography>

              <Card
                component="form"
                onSubmit={handleSubmit}
                sx={{
                  p: 4,
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                  border: "1px solid rgba(187, 0, 0, 0.1)",
                  transition: "transform 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "rgba(187, 0, 0, 0.3)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#bb0000",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "rgba(187, 0, 0, 0.3)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#bb0000",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "rgba(187, 0, 0, 0.3)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#bb0000",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Message"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "&:hover fieldset": {
                            borderColor: "rgba(187, 0, 0, 0.3)",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#bb0000",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{
                        background: "#bb0000",
                        py: 1.5,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          background: "#990000",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(187, 0, 0, 0.2)",
                        },
                      }}
                      disabled={mutation.isLoading}
                    >
                      {mutation.isLoading ? "Sending..." : "Send Message"}
                    </Button>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* Contact Info & Map Section */}
            <Grid item xs={12} md={6}>
              <Box sx={{ position: "relative" }}>
                <Card
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.05)",
                    border: "1px solid rgba(187, 0, 0, 0.1)",
                    mb: 4,
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <LocationOnIcon
                          sx={{ color: "#bb0000", fontSize: 24 }}
                        />
                        <Typography
                          variant="h6"
                          sx={{ ml: 2, color: "#555555" }}
                        >
                          Our Location
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ color: "#666666" }}>
                        Baluwatar, Kathmandu, Nepal
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <PhoneIcon sx={{ color: "#bb0000", fontSize: 24 }} />
                        <Typography
                          variant="h6"
                          sx={{ ml: 2, color: "#555555" }}
                        >
                          Phone
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ color: "#666666" }}>
                        +977 9851144390
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <EmailIcon sx={{ color: "#bb0000", fontSize: 24 }} />
                        <Typography
                          variant="h6"
                          sx={{ ml: 2, color: "#555555" }}
                        >
                          Email
                        </Typography>
                      </Box>
                      <Typography variant="body1" sx={{ color: "#666666" }}>
                        vastuplan@gmail.com
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>

                <Box
                  component="iframe"
                  src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d946.1845460364531!2d85.33005976960415!3d27.723178485664395!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMjfCsDQzJzIzLjEiTiA4NcKwMTknNTAuNyJF!5e1!3m2!1sen!2snp!4v1739981695514!5m2!1sen!2snp"
                  sx={{
                    width: "100%",
                    height: "300px",
                    border: 0,
                    borderRadius: 4,
                    boxShadow: "0 20px 40px rgba(187, 0, 0, 0.1)",
                  }}
                  allowFullScreen
                  loading="lazy"
                />
              </Box>
            </Grid>
          </Grid>
        </Fade>
      </Container>
      <Footer />
    </Box>
  );
};

export default ContactPage;
